import React, { Suspense, useEffect } from "react";

import Spinner from "@atlaskit/spinner";
import { QueryClientProvider, useQueries } from "@tanstack/react-query";
import { requestJira, view } from "@forge/bridge";
import { getThemeStyles } from "@atlaskit/tokens";
import "@atlaskit/css-reset";
import "./i18n";

import { AppContext } from "./Context";
import { queryClient } from "./Shared/functions";
import { ThinSpinnerContainer } from "./Shared/styled/components";
import AnalyticsWrapper from "./AnalyticsWrapper";
import SuspenseFallback from "./Shared/Components/SuspenseFallback";

view.theme.enable();

const DEBUGGING_FLAG = "shoppie-debug-flag";

window.debugging = localStorage.getItem(DEBUGGING_FLAG) || false;

window.enableDebugging = () => {
  window.debugging = true;
  localStorage.setItem(DEBUGGING_FLAG, "enabled");
};

window.disableDebugging = () => {
  localStorage.removeItem(DEBUGGING_FLAG);
};

function ForgeContextWrapper({
  children,
  requiresLicense,
  showSpinnerWhileLoading,
  initFunctions,
}) {
  useEffect(() => {
    getThemeStyles({
      colorMode: "light",
      light: "light",
      dark: "dark",
      spacing: "spacing",
    })
      .then((data) => {
        const lightThemeCssRule = data.find(({ id }) => id === "light")?.css;
        const betweenCurlyBracesRegex = /{[^}]*}/g;
        const extraction = betweenCurlyBracesRegex.exec(lightThemeCssRule);
        return extraction[0];
      })
      .then((stylesheet) => {
        const style = document.createElement("style");
        document.head.appendChild(style);
        style.textContent = `.light-mode-preview ${stylesheet}`;
      });
  }, []);

  const contextQueries = useQueries({
    queries: [
      {
        queryKey: ["getForgeContext"],
        queryFn: view.getContext,
        retry: false,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
      },
      {
        queryKey: ["getContext"],
        retry: false,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
      },
      {
        queryKey: ["atlassian-locale"],
        queryFn: async () => {
          try {
            const response = await requestJira(
              "/rest/api/3/mypreferences/locale"
            );
            const result = await response.json();
            const { locale = "en-US" } = result || {};
            return locale.replace("_", "-");
          } catch (error) {
            return "en-US";
          }
        },
      },
    ],
  });

  const initFunctionsQueries = useQueries({
    queries: initFunctions.map(({ fKey, data }) => ({
      queryKey: [fKey, data],
      retry: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    })),
  });

  const [forgeContextQuery, initQuery, atlassianLocaleQuery] = contextQueries;

  if (
    contextQueries.some(({ isFetching }) => isFetching) ||
    initFunctionsQueries.some(({ isFetching }) => isFetching)
  )
    return showSpinnerWhileLoading ? (
      <ThinSpinnerContainer>
        <Spinner size="large" />
      </ThinSpinnerContainer>
    ) : (
      <></>
    );

  const modalContext =
    forgeContextQuery.data?.extension?.modal ||
    forgeContextQuery.data?.extension; // in JSM modal has no "modal" property, but all context variables from calling "new Modal()" go into extension for some reason

  if (!initQuery?.data?.isLicensed && requiresLicense) return <></>;

  const contextProps = {
    forgeContext: {
      ...forgeContextQuery.data,
      localeCode: atlassianLocaleQuery?.data || "en-US",
    },
    ...initFunctionsQueries.reduce(
      (acc, { data }, ix) => ({
        ...acc,
        [initFunctions[ix].name]: data,
      }),
      {}
    ),
    modalContext,
    ...(initQuery?.data || {}),
  };

  return (
    <AppContext.Provider value={{ ...contextProps }}>
      {children}
    </AppContext.Provider>
  );
}

export function Entry({
  children,
  fetchLicense = true,
  requiresLicense = false,
  showSpinnerWhileLoading = true,
  initFunctions = [],
}) {
  return (
    <Suspense fallback={<SuspenseFallback />}>
      <QueryClientProvider client={queryClient}>
        <ForgeContextWrapper
          fetchLicense={fetchLicense}
          requiresLicense={requiresLicense}
          showSpinnerWhileLoading={showSpinnerWhileLoading}
          initFunctions={initFunctions}
        >
          <AnalyticsWrapper>{children}</AnalyticsWrapper>
        </ForgeContextWrapper>
      </QueryClientProvider>
    </Suspense>
  );
}
